// Safe application of a creator's vetted brand tokens (KAT-301).
//
// The server (internal/shared/theme) is the authority: it sanitizes every
// customizer value before it is stored, so the tokens this module receives are
// already clean. This module is the second half of that contract — and a second
// line of defense:
//
//   - It maps each allowlisted token to a specific CSS custom property.
//   - Color values are re-validated here (hex only) so a value that somehow
//     bypassed the server (compromised API, MITM) is still dropped, not applied.
//   - Enum tokens (font, radius) resolve to concrete CSS values WE control;
//     the creator's keyword only selects among them — their string never
//     becomes the CSS value.
//
// The result is a plain style object of CSS custom properties. Consumers spread
// it into a React `style` prop, so React applies each value via the DOM style
// API (setProperty for custom properties) — never as parsed markup or a raw
// stylesheet string. A malformed value can, at worst, be an ignored CSS value;
// it can never create an element or run script.

// Hex color: #rgb, #rgba, #rrggbb, #rrggbbaa. Mirrors the server's hex grammar.
const HEX_COLOR = /^#(?:[0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/

type ColorToken = { cssVar: string; kind: 'color' }
type EnumToken = { cssVar: string; kind: 'enum'; values: Record<string, string> }
type TokenSpec = ColorToken | EnumToken

// The font stacks and radius lengths below are values the product controls.
// A creator's enum choice only picks one of them by key.
const FONT_STACKS: Record<string, string> = {
  sans: "'Instrument Sans', system-ui, -apple-system, sans-serif",
  serif: "'Instrument Serif', Georgia, 'Times New Roman', serif",
  mono: "'JetBrains Mono', ui-monospace, 'SFMono-Regular', monospace",
  rounded: "'Manrope', system-ui, -apple-system, sans-serif",
  slab: "'Instrument Serif', 'Roboto Slab', Georgia, serif",
}

const RADII: Record<string, string> = {
  none: '0px',
  sm: '6px',
  md: '10px',
  lg: '16px',
  full: '999px',
}

// THEME_TOKEN_SCHEMA is the single source of truth for what a creator may
// customize, mirroring the Go schema in internal/shared/theme. Adding a token
// is a deliberate edit in both places.
const THEME_TOKEN_SCHEMA: Record<string, TokenSpec> = {
  brandPrimary: { cssVar: '--c-accent', kind: 'color' },
  brandAccent: { cssVar: '--c-accent-soft', kind: 'color' },
  surface: { cssVar: '--c-bg', kind: 'color' },
  surfaceText: { cssVar: '--c-fg', kind: 'color' },
  // Header bar overrides. The learner-site header (LearnerShell) reads these
  // with a fallback to the theme's elevated surface / foreground, so they only
  // take effect when a creator sets them.
  headerBg: { cssVar: '--c-header-bg', kind: 'color' },
  headerText: { cssVar: '--c-header-fg', kind: 'color' },
  fontFamily: { cssVar: '--font-sans', kind: 'enum', values: FONT_STACKS },
  radius: { cssVar: '--r-md', kind: 'enum', values: RADII },
}

export const THEME_TOKEN_NAMES = Object.keys(THEME_TOKEN_SCHEMA)

// themeTokenStyle turns a creator's sanitized token map into a style object of
// CSS custom properties. Unknown tokens and values that fail re-validation are
// dropped silently — the site falls back to the theme default rather than
// applying anything unsafe.
export function themeTokenStyle(tokens?: Record<string, string> | null): Record<string, string> {
  const style: Record<string, string> = {}
  if (!tokens) {
    return style
  }
  for (const [name, raw] of Object.entries(tokens)) {
    const spec = THEME_TOKEN_SCHEMA[name]
    if (!spec || typeof raw !== 'string') {
      continue
    }
    const value = raw.trim()
    if (spec.kind === 'color') {
      if (HEX_COLOR.test(value)) {
        style[spec.cssVar] = value
      }
      continue
    }
    // enum: the keyword must resolve to one of our controlled CSS values.
    const resolved = spec.values[value]
    if (resolved !== undefined) {
      style[spec.cssVar] = resolved
    }
  }
  return style
}
